import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SectionTitle from '@/components/feature/SectionTitle';
import { aboutInfo } from '@/mocks/about';
import { comments } from '@/mocks/comments';

export default function AboutPage() {
  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于我们 - ${aboutInfo.brandName}`,
    description: aboutInfo.description,
    url: 'http://lalaxjk.cn/about',
    mainEntity: {
      '@type': 'Organization',
      name: aboutInfo.brandName,
      description: aboutInfo.description,
      url: 'http://lalaxjk.cn/',
      sameAs: ['http://lalaxjk.cn/'],
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      <Navbar />
      <main className="pt-16">
        {/* Hero */}
        <section className="relative w-full py-16 md:py-24 bg-foreground-950">
          <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]}
            />
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-background-50 mt-8 mb-4">
              关于 {aboutInfo.brandName}
            </h1>
            <p className="text-lg text-background-300 max-w-2xl mx-auto leading-relaxed">
              {aboutInfo.slogan} — {aboutInfo.mission}
            </p>
          </div>
        </section>

        {/* Brand Intro */}
        <section className="py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <div>
                <div className="relative rounded-2xl overflow-hidden aspect-[4/3]">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20digital%20newsroom%20with%20multiple%20screens%20showing%20social%20media%20feeds%2C%20warm%20ambient%20lighting%2C%20red%20and%20amber%20accent%20lights%2C%20sleek%20minimalist%20workspace%2C%20editorial%20photography%20style&width=800&height=600&seq=about-brand&orientation=landscape"
                    alt="51黑料网品牌"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-3 left-3 flex gap-2">
                    <span className="px-2.5 py-1 bg-background-50/90 backdrop-blur-sm text-foreground-800 text-xs font-semibold rounded-md">
                      成立于2024
                    </span>
                    <span className="px-2.5 py-1 bg-primary-500/90 backdrop-blur-sm text-background-50 text-xs font-semibold rounded-md">
                      行业领先
                    </span>
                  </div>
                </div>
              </div>
              <div>
                <SectionTitle title="品牌介绍" subtitle="BRAND" />
                <p className="text-base text-foreground-600 leading-relaxed mb-6">
                  {aboutInfo.description}
                </p>
                <p className="text-base text-foreground-600 leading-relaxed mb-8">
                  我们相信，信息透明是推动行业健康发展的重要力量。51黑料网以专业、客观、深度的态度，为每一位关注网红圈与娱乐圈动态的读者提供最有价值的内容。
                </p>
                <div className="bg-foreground-950 rounded-2xl p-6 md:p-8">
                  <p className="text-sm text-background-400 mb-4">平台核心数据</p>
                  <div className="grid grid-cols-2 gap-4">
                    {aboutInfo.stats.map((stat) => (
                      <div key={stat.label}>
                        <p className="text-2xl md:text-3xl font-bold text-background-50">{stat.value}</p>
                        <p className="text-xs text-background-400 mt-1">{stat.label}</p>
                        <p className="text-xs text-background-500">{stat.suffix}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <SectionTitle
              title="核心优势"
              subtitle="ADVANTAGES"
              align="center"
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {aboutInfo.features.map((feature) => (
                <div
                  key={feature.title}
                  className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200/70 hover:border-primary-200 transition-all hover:shadow-md text-center"
                >
                  <div className="w-14 h-14 rounded-xl bg-primary-50 flex items-center justify-center mx-auto mb-5">
                    <i className={`${feature.icon} text-2xl text-primary-600`} />
                  </div>
                  <h3 className="text-lg font-bold text-foreground-900 mb-3">{feature.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* User Reviews */}
        <section className="py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <SectionTitle
              title="用户评价"
              subtitle="TESTIMONIALS"
              align="center"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {comments.slice(0, 6).map((comment) => (
                <div
                  key={comment.id}
                  className="bg-background-50 rounded-xl border border-background-200/70 p-5 hover:border-primary-200 transition-all"
                >
                  <div className="flex items-center gap-0.5 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <span key={i} className="text-accent-400 text-sm">
                        {i < comment.rating ? (
                          <i className="ri-star-fill" />
                        ) : (
                          <i className="ri-star-line" />
                        )}
                      </span>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">{comment.content}</p>
                  <div className="flex items-center gap-3">
                    <img
                      src={comment.avatar}
                      alt={comment.username}
                      className="w-10 h-10 rounded-full object-cover bg-background-200"
                      loading="lazy"
                    />
                    <div>
                      <p className="text-sm font-semibold text-foreground-800">{comment.username}</p>
                      <p className="text-xs text-foreground-400">{comment.date}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
            <SectionTitle
              title="联系我们"
              subtitle="CONTACT"
              align="center"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mt-8">
              <div className="bg-background-50 rounded-xl p-6 border border-background-200/70">
                <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-mail-line text-primary-600" />
                </div>
                <p className="text-sm font-medium text-foreground-700">官方邮箱</p>
                <p className="text-xs text-foreground-500 mt-1">{aboutInfo.contact.email}</p>
              </div>
              <div className="bg-background-50 rounded-xl p-6 border border-background-200/70">
                <div className="w-10 h-10 rounded-lg bg-accent-50 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-wechat-line text-accent-600" />
                </div>
                <p className="text-sm font-medium text-foreground-700">微信公众号</p>
                <p className="text-xs text-foreground-500 mt-1">{aboutInfo.contact.wechat}</p>
              </div>
              <div className="bg-background-50 rounded-xl p-6 border border-background-200/70">
                <div className="w-10 h-10 rounded-lg bg-secondary-50 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-weibo-line text-secondary-600" />
                </div>
                <p className="text-sm font-medium text-foreground-700">新浪微博</p>
                <p className="text-xs text-foreground-500 mt-1">{aboutInfo.contact.weibo}</p>
              </div>
              <div className="bg-background-50 rounded-xl p-6 border border-background-200/70">
                <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-map-pin-line text-primary-600" />
                </div>
                <p className="text-sm font-medium text-foreground-700">运营地址</p>
                <p className="text-xs text-foreground-500 mt-1">{aboutInfo.contact.address}</p>
              </div>
            </div>

            <div className="mt-10 bg-accent-50 rounded-xl border border-accent-200/50 p-6">
              <h4 className="text-sm font-semibold text-accent-900 mb-2">免责声明</h4>
              <p className="text-xs text-foreground-500 leading-relaxed">
                {aboutInfo.disclaimer}
              </p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
